import { useLazyQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { GetLalSegmentsBuildAudienceDocument } from '../../../../../../../../../../graphql/query/audiences/GetLalSegmentsBuildAudience.generated';
import { buildLookalikeFilters } from '../../../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/hooks/utils/lookalikeFilterBuilder';
import { transformLalDefinitions } from '../../../../../../../../../../modules/audiences/AudienceForm/components/AudienceCriteria/hooks/utils/transformLalDefinitions';
import type { LookalikeSegmentDefinition } from '../../../../../../../../../../modules/audiences/AudienceForm/types';
import type { UseSegmentPagination } from '../../../hooks/types';
import { ActionType, createInitialPaginationState, paginationReducer } from './utils/paginationReducer';

const LOOKALIKE_PAGE_SIZE = 50;

export const useLookalikePagination: UseSegmentPagination<LookalikeSegmentDefinition> = ({
  definitions,
  accountId = '',
  linkedProviders,
  searchPhrase,
}) => {
  const [state, dispatch] = useReducer(
    paginationReducer<LookalikeSegmentDefinition>(),
    createInitialPaginationState<LookalikeSegmentDefinition>(definitions || [], definitions?.length || 0)
  );

  const [loadMoreDefinitions, { loading: loadingMore }] = useLazyQuery(GetLalSegmentsBuildAudienceDocument);

  const prevSearchPhraseRef = useRef(searchPhrase);
  const prevDefinitionsRef = useRef<LookalikeSegmentDefinition[]>(definitions || []);

  useEffect(() => {
    if (!isEqual(prevDefinitionsRef.current, definitions)) {
      dispatch({ type: ActionType.Reset, items: definitions || [], totalCount: definitions?.length });
      prevDefinitionsRef.current = definitions || [];
    }
  }, [definitions]);

  useEffect(() => {
    if (prevSearchPhraseRef.current !== searchPhrase) {
      dispatch({ type: ActionType.SetOffset, offset: 0 });
      prevSearchPhraseRef.current = searchPhrase;
    }
  }, [searchPhrase]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || state.isManualLoading) {
      return;
    }
    dispatch({ type: ActionType.StartLoading });

    const nextOffset = state.offset + LOOKALIKE_PAGE_SIZE;
    dispatch({ type: ActionType.SetOffset, offset: nextOffset });

    try {
      const { where } = buildLookalikeFilters({
        filters: {
          phrase: searchPhrase,
          limit: LOOKALIKE_PAGE_SIZE,
          offset: nextOffset,
        },
        accountId,
        linkedProviders: linkedProviders || [],
      });

      const { data } = await loadMoreDefinitions({
        variables: {
          where,
          limit: LOOKALIKE_PAGE_SIZE,
          offset: nextOffset,
          order_by: [{ updated_date: Order_By.Desc }],
        },
      });

      const newDefinitions = transformLalDefinitions(accountId, data?.lalSegmentFeed);

      dispatch({
        type: ActionType.AppendItems,
        items: newDefinitions,
        totalCount: (state.totalCount || 0) + newDefinitions.length,
      });
    } catch {
      dispatch({ type: ActionType.SetOffset, offset: nextOffset - LOOKALIKE_PAGE_SIZE });
    } finally {
      dispatch({ type: ActionType.FinishLoading });
    }
  }, [
    loadMoreDefinitions,
    accountId,
    linkedProviders,
    searchPhrase,
    loadingMore,
    state.offset,
    state.isManualLoading,
    state.totalCount,
  ]);

  return {
    loadingMore: loadingMore || state.isManualLoading,
    allSegments: state.items,
    totalCount: state.totalCount || 0,
    handleLoadMore,
  };
};
